import { Component, ElementRef, ViewChild, AfterViewInit, OnDestroy, OnInit, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Router } from '@angular/router';
import { FaqComponent } from '../../../shared/faq/faq.component';
import { FaqItem } from '../../../models/faq.model';
import { ExploreMoreIndustriesComponent } from '../explore-more-industries/explore-more-industries.component';
import { trigger, state, style, animate, transition, query, stagger } from '@angular/animations';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ContactUsComponent } from '../../../shared/contact-us/contact-us.component';

interface CaseStudy {
  image: string;
  title: string;
  description: string;
}

@Component({
  selector: 'app-insurance',
  imports: [CommonModule, RouterModule, FaqComponent, ExploreMoreIndustriesComponent],
  standalone: true,
  templateUrl: './insurance.component.html',
  styleUrl: './insurance.component.scss',
  animations: [
    trigger('fadeInUpStagger', [
      transition(':enter', [
        // This is a placeholder, actual logic handled via state binding below
      ]),
      transition('* => visible', [
        query('.animate-item', [
          style({ opacity: 0, transform: 'translateY(30px)' }),
          stagger('200ms', [
            animate('800ms cubic-bezier(0.35, 0, 0.25, 1)', style({ opacity: 1, transform: 'translateY(0)' }))
          ])
        ], { optional: true })
      ])
    ])
  ]
})
export class InsuranceComponent implements AfterViewInit, OnInit, OnDestroy {
  private jsonLdScript: any;
  @ViewChild('heroVideo') heroVideo!: ElementRef<HTMLVideoElement>;
  @ViewChild('impactSection') impactSection!: ElementRef;
  impactSectionVisible = false; // Flag to control animation
  private observer: IntersectionObserver | null = null;

  caseStudies: CaseStudy[] = [
    {
      image: '../../../../assets/industries/insurtech/insurtech3.png',
      title: 'AI-Powered Insurance Onboarding Accelerator ',
      description: 'Kalpita Technologies partnered with a leading insurance provider to streamline and modernize its customer onboarding process through an AI-Driven Insurance Application Accelerator. The solution automated complex questionnaires, centralized evaluation, and integrated backend systems such as Blueprint, reducing manual efforts by 70% and cutting processing time nearly in half. This transformation not only boosted data accuracy and efficiency but also enhanced customer satisfaction, scalability, and conversion rates for new policies.'
    },
    {
      image: '../../../../assets/industries/insurtech/insurtech2.png',
      title: 'Automated Billing Solution for Insurance Efficiency',
      description: 'Kalpita Technologies developed a scalable automated billing system integrated with Azure cloud functionalities to overcome manual errors, billing inconsistencies, and delayed notifications faced by a top-tier insurance company. The system supports multiple billing cycles, handles cancellations and renewals, and offers a user-friendly interface for real-time billing status management. This solution enhanced 100% accuracy and customer engagement through proactive alerts and transparent billing history, transforming the billing process into an efficient, error-free operation that meets the evolving needs of the insurance industry.'
    },
    {
      image: '../../../../assets/industries/insurtech/insurtech1.png',
      title: 'Unified Customer Engagement Portal',
      description: 'Kalpita Technologies partnered with a top-tier insurance provider to modernize their customer engagement ecosystem through a unified digital portal. The platform consolidated multiple service lines into one secure, SOC2 and GDPR compliant environment, enabling seamless client–advisor collaboration, consistent brand communication, and actionable business insights via integrated Power BI dashboards. This transformation enhanced customer retention, improved decision-making, and reinforced the client’s brand identity across all insurance verticals.'
    }
  ];
  private forcePlayHeroVideo() {
    const video = this.heroVideo?.nativeElement;
    if (!video) return;

    video.muted = true;           // required for autoplay
    video.playsInline = true;

    const play = () => {
      video.play().catch(() => {
        // retry if browser blocks
        setTimeout(() => video.play().catch(() => { }), 300);
      });
    };

    // Ensure it runs after Angular rendering
    setTimeout(play, 100);
  }

  InsuranceFaqs: FaqItem[] = [
    {
      question: 'What is Insurtech software development?',
      answer: `Insurtech software development involves creating digital tools, platforms, 
      and automation solutions that help insurers modernize operations and deliver faster, 
      more accurate, and more customer-centric insurance services.`
    },
    {
      question: 'How can Insurtech improve insurance operations?',
      answer: `By integrating automation, AI, and digital workflows, insurers can reduce manual tasks, 
      accelerate claims processing, improve underwriting accuracy, and deliver seamless policyholder 
      experiences.`
    },
    {
      question: 'How do I choose the right Insurtech software development partner?',
      answer: `Look for a team experienced in custom insurance software, compliance standards, 
      cloud technologies, scalability requirements, and AI-driven insurance solutions, along with 
      strong domain knowledge and proven delivery expertise.`
    },
    {
      question: 'Can you modernize our legacy Insurance system?',
      answer: `Absolutely. We specialize in transforming legacy systems into secure, 
      cloud-native digital insurance platforms with improved performance, scalability, and 
      customer experience.`
    },
    {
      question: 'What is AI-accelerated software development?',
      answer: `AI-accelerated development uses artificial intelligence to speed up architecture design, 
      code generation, testing, and integration - reducing development time by 50-60% while maintaining 
      high quality standards. This approach allows businesses to launch new capabilities in weeks 
      or months rather than years.`
    },
    {
      question: 'How quickly can you deliver a custom Insurtech solution?',
      answer: `Timelines vary by project scope, but our AI-accelerated approach typically 
      delivers production-ready solutions in 6-9 months for comprehensive platforms - 60% faster 
      than traditional development. We provide realistic timelines during initial consultation based 
      on your specific requirements.`
    },
    {
      question: 'Do your solutions comply with insurance industry regulations?',
      answer: `Yes. All solutions are built with compliance at the core, including SOC2, GDPR, 
      HIPAA (for health insurance), state insurance regulations, and industry-specific data 
      protection standards. We have deep expertise in navigating complex regulatory requirements.`
    }
  ];

  currentSlide = 0;
  isDragging = false;
  startPosition = 0;
  currentPosition = 0;
  transformValue = 0;
  private autoSlideInterval: any;
  private readonly SLIDE_DURATION = 5000;

  constructor(private router: Router,
    private modalService: NgbModal,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) { }

  ngOnDestroy(): void {
    this.removeSchemaMarkup();
    this.stopAutoSlide();
    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
    }
    if (this.observer) {
      this.observer.disconnect();
    }

  }

  ngAfterViewInit(): void {
    this.forcePlayHeroVideo();
    // 1. Setup Intersection Observer for Text Animation
    const options = {
      root: null,
      threshold: 0.2,
      rootMargin: '0px'
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.impactSectionVisible = true;
          this.observer?.disconnect();
        }
      });
    }, options);

    if (this.impactSection) {
      this.observer.observe(this.impactSection.nativeElement);
    }

    // 2. Initialize the 3D Tilt Effect
    this.setupTiltEffect();
  }

  // --- NEW: 3D TILT LOGIC (Copied from Software Development) ---
  setupTiltEffect(): void {
    // Target classes: Solution Cards, Impact Image, and Case Study Images
    const cards = document.querySelectorAll('.solution-image-card, .impact-image-wrapper, .case-study-image-wrapper');

    cards.forEach(card => {
      const cardEl = card as HTMLElement;

      const mouseMoveHandler = (e: MouseEvent) => {
        const rect = cardEl.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Calculate center
        const xc = rect.width / 2;
        const yc = rect.height / 2;

        // Max rotation degrees
        const maxRotate = 10;

        // Calculate rotation based on cursor position
        const rotateY = ((x - xc) / xc) * maxRotate;
        const rotateX = ((yc - y) / yc) * maxRotate;

        // Apply transform
        cardEl.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
      };

      const mouseLeaveHandler = () => {
        // Reset to flat state
        cardEl.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
      };

      const mouseEnterHandler = () => {
        // Smooth entry config
        cardEl.style.transition = 'transform 0.1s ease-out, box-shadow 0.2s ease-in-out';
      };

      // Attach Listeners
      cardEl.addEventListener('mousemove', mouseMoveHandler as EventListener);
      cardEl.addEventListener('mouseleave', mouseLeaveHandler as EventListener);
      cardEl.addEventListener('mouseenter', mouseEnterHandler as EventListener);

      // Disable on touch devices
      if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
        cardEl.removeEventListener('mousemove', mouseMoveHandler as EventListener);
        cardEl.style.transform = 'none';
      }
    });
  }

  getStarted(): void {

  }

  scheduleDemo(): void {

  }
  startAutoSlide(): void {
    this.stopAutoSlide(); // Ensure no duplicate intervals
    this.autoSlideInterval = setInterval(() => {
      this.nextSlide();
    }, this.SLIDE_DURATION);
  }

  stopAutoSlide(): void {
    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
    }
  }

  nextSlide(): void {
    if (this.currentSlide < this.caseStudies.length - 1) {
      this.currentSlide++;
    } else {
      this.currentSlide = 0; // Loop back to start
    }
  }

  goToSlide(index: number): void {
    this.currentSlide = index;
    // Reset timer when user manually clicks a dot
    this.stopAutoSlide();
    this.startAutoSlide();
  }

  // UX: Pause slider when user hovers to read text
  onMouseEnter(): void {
    this.stopAutoSlide();
  }

  onMouseLeave(): void {
    this.startAutoSlide();
  }
  // Mouse event handlers
  onMouseDown(event: MouseEvent): void {
    this.isDragging = true;
    this.startPosition = event.clientX;
    this.currentPosition = this.startPosition;
    clearInterval(this.autoSlideInterval);
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;
    this.currentPosition = event.clientX;
    this.transformValue = -this.currentSlide * 100 + ((this.currentPosition - this.startPosition) / window.innerWidth * 100);
  }

  // Touch event handlers
  onTouchStart(event: TouchEvent): void {
    this.isDragging = true;
    this.startPosition = event.touches[0].clientX;
    this.currentPosition = this.startPosition;
    clearInterval(this.autoSlideInterval);
  }

  onTouchMove(event: TouchEvent): void {
    if (!this.isDragging) return;
    this.currentPosition = event.touches[0].clientX;
    this.transformValue = -this.currentSlide * 100 + ((this.currentPosition - this.startPosition) / window.innerWidth * 100);
  }

  redirectToAbout(): void {
    this.router.navigate(['/about-us']);
  }

  ngOnInit(): void {
    this.injectSchemaMarkup();
    this.startAutoSlide();
  }
  injectSchemaMarkup(): void {
    const insurtechSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/industries/insurtech#webpage",
          "url": "https://www.kalpitatechnologies.com/industries/insurtech",
          "name": "Insurtech Software Development Solutions | AI-Powered Insurance Platform | Kalpita Technologies",
          "description": "End-to-end insurance software development solutions by Kalpita Technologies. AI-accelerated development for claims processing, policy management, and underwriting.",
          "isPartOf": { "@id": "https://www.kalpitatechnologies.com/#website" },
          "inLanguage": "en",
          "datePublished": "2026-01-22"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/industries/insurtech#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com/" },
            { "@type": "ListItem", "position": 2, "name": "Industries", "item": "https://www.kalpitatechnologies.com/industries" },
            { "@type": "ListItem", "position": 3, "name": "Insurtech", "item": "https://www.kalpitatechnologies.com/industries/insurtech" }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://www.kalpitatechnologies.com/industries/insurtech#service",
          "name": "Insurtech Software Development Solutions",
          "serviceType": "Insurance Software Development",
          "provider": { "@id": "https://www.kalpitatechnologies.com/#organization" },
          "description": "End-to-end insurtech software development services empowering insurance companies to modernize operations through AI-accelerated development..."
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/industries/insurtech#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is Insurtech software development?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Insurtech software development involves creating digital tools, platforms, and automation solutions that help insurers modernize operations and deliver faster services."
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(insurtechSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  onCardClick(index: number): void {
    console.log('Clicked card', index);
    // navigate or open modal
  }

  getStarArray(rating: number): boolean[] {
    return Array(5).fill(false).map((_, index) => index < rating);
  }

  freeConsultation(): void {
    this.modalService.open(ContactUsComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'contact-modal'
    });
  }

  testimonials(): void {
    this.router.navigate(['/'], { fragment: 'testimonials' });
  }
}
